import { useState } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { AlertTriangle, CheckCircle2, Save } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/stores/toastStore';
import { paieComptaApi, type ComptePaie, type PostePaie } from '@/api/paie';

/**
 * PAIE-5 — sur quel compte va chaque poste de l'écriture de paie (DAF et PDG). Numéros SYSCOHADA usuels proposés,
 * à valider par le DAF ; les comptes absents sont créés au plan comptable de la société à la validation.
 */
export const ComptesPaie = () => {
  const qc = useQueryClient();
  const cle = ['paie', 'comptes'];
  const q = useQuery({ queryKey: cle, queryFn: paieComptaApi.comptes });
  const maj = (d: ComptePaie[]) => qc.setQueryData(cle, d);
  const [saisie, setSaisie] = useState<Record<string, { code: string; libelle: string }>>({});
  const enregistrer = useMutation({
    mutationFn: ({ poste, code, libelle }: { poste: PostePaie; code: string; libelle: string }) => paieComptaApi.modifierCompte(poste, code, libelle),
    onSuccess: (d, v) => { maj(d); setSaisie(({ [v.poste]: _, ...r }) => r); toast.success('Compte modifié', 'À revalider'); },
  });
  const valider = useMutation({ mutationFn: paieComptaApi.validerComptes, onSuccess: (d) => { maj(d); toast.success('Comptes de paie validés'); } });
  const lignes = q.data ?? [];
  const aValider = lignes.some((l) => !l.valideLe);
  const demanderValidation = async () => {
    const absents = lignes.filter((l) => !l.compteExistant && l.compteCode).map((l) => l.compteCode);
    const msg = absents.length ? `Ces comptes seront créés au plan comptable : ${[...new Set(absents)].join(', ')}.` : undefined;
    if (await demander.confirmer({ titre: 'Valider ces comptes de paie ?', message: msg, confirmer: 'Valider', danger: true })) valider.mutate();
  };

  return (
    <div className="ds-table-panel">
      <div className="ds-table-header">
        <div><div className="ds-table-title">Comptes de l’écriture de paie</div>
          <div className="ds-table-sub">Numéros SYSCOHADA proposés : à faire valider par le DAF (ou le cabinet) avant la première comptabilisation.</div></div>
        {aValider && <button className="ds-btn ds-btn-p ds-btn-sm" disabled={valider.isPending || Object.keys(saisie).length > 0} onClick={demanderValidation}><CheckCircle2 size={13} /> Valider les comptes</button>}
      </div>
      {aValider && <div style={{ margin: '0 16px 8px', fontSize: 12, color: '#92400E' }}><AlertTriangle size={12} style={{ verticalAlign: -2 }} /> Tant que ce tableau n’est pas validé, aucune paie ne se comptabilise.</div>}
      <div style={{ overflowX: 'auto' }}>
        <table className="ds-tbl" style={{ minWidth: 640 }}>
          <thead><tr><th>Poste</th><th>Compte</th><th>Libellé</th><th>Au plan comptable</th><th>État</th><th /></tr></thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={6}>Chargement…</td></tr>}
            {lignes.map((l) => {
              const s = saisie[l.poste];
              return (
                <tr key={l.poste}>
                  <td style={{ fontSize: 12 }}>{l.libellePoste}</td>
                  <td><input className="ds-fi" style={{ width: 90 }} inputMode="numeric" maxLength={6} value={s?.code ?? l.compteCode ?? ''}
                    onChange={(e) => setSaisie({ ...saisie, [l.poste]: { code: e.target.value.replace(/\D/g, ''), libelle: s?.libelle ?? l.libelleCompte ?? '' } })} /></td>
                  <td><input className="ds-fi" value={s?.libelle ?? l.libelleCompte ?? ''}
                    onChange={(e) => setSaisie({ ...saisie, [l.poste]: { code: s?.code ?? l.compteCode ?? '', libelle: e.target.value } })} /></td>
                  <td style={{ fontSize: 12 }}>{l.compteExistant ? l.libelleAuPlan : <span style={{ color: '#92400E' }}>absent — créé à la validation</span>}</td>
                  <td>{l.valideLe ? <span className="ds-bd ds-bd-s">validé</span> : <span className="ds-bd ds-bd-w">proposé</span>}</td>
                  <td>{s && <button className="ds-btn ds-btn-g ds-btn-sm" disabled={enregistrer.isPending || !/^[1-9]\d{5}$/.test(s.code) || s.libelle.trim().length < 3}
                    onClick={() => enregistrer.mutate({ poste: l.poste, code: s.code, libelle: s.libelle })}><Save size={12} /></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
